import { useState, useEffect, useRef, useCallback } from 'react'
import { apiService } from '../../services/api'
import { PhotoTheme, PhotoSubmission } from '../../types'

interface BatchFile {
  id: string
  file: File
  caption: string
  status: 'pending' | 'uploading' | 'done' | 'error'
}

export default function PhotoAdmin() {
  const [themes, setThemes] = useState<PhotoTheme[]>([])
  const [selectedTheme, setSelectedTheme] = useState<PhotoTheme | null>(null)
  const [submissions, setSubmissions] = useState<PhotoSubmission[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [selectedSubmissions, setSelectedSubmissions] = useState<Set<string>>(new Set())
  const [formData, setFormData] = useState({ date: new Date().toISOString().split('T')[0], title: '', description: '', type: 'pilot' as 'pilot' | 'official' })
  const [imgErrors, setImgErrors] = useState<Set<string>>(new Set())
  const [batchFiles, setBatchFiles] = useState<BatchFile[]>([])
  const [batchUploading, setBatchUploading] = useState(false)
  const [dragOver, setDragOver] = useState(false)
  const [filterDate, setFilterDate] = useState('')
  const [filterYear, setFilterYear] = useState('')
  const [submissionCounts, setSubmissionCounts] = useState<Record<string, number>>({})
  const fileInputRef = useRef<HTMLInputElement>(null)
  const detailRef = useRef<HTMLDivElement>(null)

  useEffect(() => { loadThemes() }, [])
  useEffect(() => { if (selectedTheme) loadSubmissions(selectedTheme.themeId) }, [selectedTheme])

  // On mobile, scroll the detail panel into view when a theme is selected
  useEffect(() => {
    if (selectedTheme && detailRef.current) {
      detailRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [selectedTheme])
  useEffect(() => {
    if (toast) { const t = setTimeout(() => setToast(null), 5000); return () => clearTimeout(t) }
  }, [toast])

  const availableYears = Array.from(new Set(themes.map(t => t.date?.slice(0, 4)).filter(Boolean))).sort().reverse()

  const filteredThemes = themes.filter(t => {
    if (filterDate && t.date !== filterDate) return false
    if (filterYear && !t.date?.startsWith(filterYear)) return false
    return true
  })

  const loadThemes = async () => {
    setLoading(true); setError(null)
    const response = await apiService.getAllThemes()
    if (response.error) { setError(response.error.message); setLoading(false); return }
    if (response.data) {
      const data = response.data as any
      const themes = data?.data?.themes || data?.themes || []
      const themeList = Array.isArray(themes) ? themes : []
      setThemes(themeList)
      // Load submission counts for all themes in parallel
      loadSubmissionCounts(themeList)
    } else { setThemes([]) }
    setLoading(false)
  }

  const loadSubmissionCounts = async (themeList: PhotoTheme[]) => {
    const counts: Record<string, number> = {}
    await Promise.all(
      themeList.map(async (theme) => {
        try {
          const r = await apiService.getThemeSubmissions(theme.themeId)
          if (r.data) {
            const d = r.data as any
            const subs = d?.data?.submissions || d?.submissions || []
            counts[theme.themeId] = Array.isArray(subs) ? subs.length : 0
          }
        } catch { /* ignore */ }
      })
    )
    setSubmissionCounts(counts)
  }

  const loadSubmissions = async (themeId: string) => {
    const response = await apiService.getThemeSubmissions(themeId)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); setSubmissions([]); return }
    if (response.data) {
      const data = response.data as any
      const submissions = data?.data?.submissions || data?.submissions || []
      const subList = Array.isArray(submissions) ? submissions : []
      setSubmissions(subList)
      setSubmissionCounts(prev => ({ ...prev, [themeId]: subList.length }))
    } else { setSubmissions([]) }
  }

  const handleCreateTheme = async (e: React.FormEvent) => {
    e.preventDefault()
    const { type, date, title, description } = formData
    const payload: { title: string; description: string; type: 'pilot' | 'official'; date?: string } = { title, description, type }
    if (date) payload.date = date
    const response = await apiService.createTheme(payload)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: 'Theme created successfully', type: 'success' })
    setFormData({ date: new Date().toISOString().split('T')[0], title: '', description: '', type: 'pilot' })
    setShowCreateForm(false); loadThemes()
  }

  const handleDeleteTheme = async (themeId: string) => {
    const response = await apiService.deleteTheme(themeId)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: 'Theme deleted successfully', type: 'success' })
    if (selectedTheme?.themeId === themeId) { setSelectedTheme(null); setSubmissions([]) }
    loadThemes()
  }

  const handlePublish = async (submissionId: string) => {
    const response = await apiService.publishSubmission(submissionId)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: 'Photo published', type: 'success' })
    if (selectedTheme) loadSubmissions(selectedTheme.themeId)
  }

  const handleUnpublish = async (submissionId: string) => {
    const response = await apiService.unpublishSubmission(submissionId)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: 'Photo unpublished', type: 'success' })
    if (selectedTheme) loadSubmissions(selectedTheme.themeId)
  }

  const handleBatchPublish = async () => {
    if (selectedSubmissions.size === 0) { setToast({ message: 'Select submissions first', type: 'error' }); return }
    const response = await apiService.batchPublishSubmissions(Array.from(selectedSubmissions))
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setToast({ message: `${selectedSubmissions.size} photos published`, type: 'success' })
    setSelectedSubmissions(new Set())
    if (selectedTheme) loadSubmissions(selectedTheme.themeId)
  }

  const handleSetWinner = async (submissionId: string) => {
    if (!selectedTheme) return
    const response = await apiService.updateTheme(selectedTheme.themeId, { winnerSubmissionId: submissionId })
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    setSelectedTheme({ ...selectedTheme, winnerSubmissionId: submissionId })
    setToast({ message: 'Winner photo set', type: 'success' })
  }

  const handleRemoveWinner = async () => {
    if (!selectedTheme) return
    const response = await apiService.updateTheme(selectedTheme.themeId, { winnerSubmissionId: null })
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    const updated = { ...selectedTheme }
    delete updated.winnerSubmissionId
    setSelectedTheme(updated)
    setToast({ message: 'Winner removed', type: 'success' })
  }

  const handleDownloadZip = async (date: string) => {
    setToast({ message: 'Preparing ZIP download — this may take a moment for large collections…', type: 'success' })
    const response = await apiService.downloadSubmissionsZip(date)
    if (response.error) { setToast({ message: response.error.message, type: 'error' }); return }
    const data = response.data as any
    const downloadUrl = data?.data?.downloadUrl || data?.downloadUrl
    if (downloadUrl) {
      // Build a friendly filename: YYMMDD_theme-name.zip
      const themeName = selectedTheme?.title?.replace(/[^a-zA-Z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase() || 'photos'
      const datePart = date.replace(/-/g, '').slice(2) // "2026-03-17" → "260317"
      const filename = `${datePart}_${themeName}.zip`

      // Fetch the zip and trigger download with the friendly filename
      try {
        const res = await fetch(downloadUrl)
        const blob = await res.blob()
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
        setToast({ message: 'Download started', type: 'success' })
      } catch {
        // Fallback to direct navigation if fetch fails
        window.location.href = downloadUrl
        setToast({ message: 'Download started', type: 'success' })
      }
    } else {
      setToast({ message: 'No download URL returned', type: 'error' })
    }
  }

  const toggleSelection = (id: string) => {
    const s = new Set(selectedSubmissions)
    if (s.has(id)) { s.delete(id) } else { s.add(id) }
    setSelectedSubmissions(s)
  }

  const addBatchFiles = useCallback((files: FileList | File[]) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'))
    const newEntries: BatchFile[] = imageFiles.map(file => ({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      file,
      caption: '',
      status: 'pending' as const,
    }))
    setBatchFiles(prev => [...prev, ...newEntries])
  }, [])

  const removeBatchFile = (id: string) => {
    setBatchFiles(prev => prev.filter(f => f.id !== id))
  }

  const updateBatchCaption = (id: string, caption: string) => {
    setBatchFiles(prev => prev.map(f => f.id === id ? { ...f, caption } : f))
  }

  const handleBatchUpload = async () => {
    if (!selectedTheme || batchFiles.length === 0) return
    setBatchUploading(true)
    setBatchFiles(prev => prev.map(f => ({ ...f, status: 'uploading' as const })))

    const photos = batchFiles.map(f => ({
      file: f.file,
      caption: f.caption || undefined,
    }))

    const response = await apiService.batchUploadPhotos(selectedTheme.themeId, photos)

    if (response.error) {
      setBatchFiles(prev => prev.map(f => ({ ...f, status: 'error' as const })))
      setToast({ message: response.error.message, type: 'error' })
    } else {
      setBatchFiles(prev => prev.map(f => ({ ...f, status: 'done' as const })))
      setToast({ message: `${batchFiles.length} photos uploaded successfully`, type: 'success' })
      setTimeout(() => setBatchFiles([]), 1500)
      loadSubmissions(selectedTheme.themeId)
    }
    setBatchUploading(false)
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) addBatchFiles(e.dataTransfer.files)
  }, [addBatchFiles])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(true)
  }, [])

  const handleDragLeave = useCallback(() => {
    setDragOver(false)
  }, [])

  if (loading) return (
    <div className="flex justify-center items-center py-12">
      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
    </div>
  )

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
      <p className="text-red-800">{error}</p>
      <button onClick={loadThemes} className="mt-2 text-red-600 hover:text-red-800 font-medium">Try Again</button>
    </div>
  )

  return (
    <div className="space-y-6">
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-white ${toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'}`}>
          <div className="flex items-center gap-2">
            <span>{toast.message}</span>
            <button onClick={() => setToast(null)} className="ml-2 text-white/80 hover:text-white">&times;</button>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Photo Competition Admin</h2>
        <button onClick={() => setShowCreateForm(!showCreateForm)}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
          {showCreateForm ? 'Cancel' : 'Create Theme'}
        </button>
      </div>

      {showCreateForm && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-bold text-gray-900 mb-4">Create New Theme</h3>
          <form onSubmit={handleCreateTheme} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Theme Type</label>
              <div className="flex gap-3">
                <button type="button"
                  onClick={() => setFormData({ ...formData, type: 'pilot' })}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                    formData.type === 'pilot'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}>
                  Pilot Theme
                </button>
                <button type="button"
                  onClick={() => setFormData({ ...formData, type: 'official' })}
                  className={`flex-1 px-4 py-2 rounded-lg border-2 text-sm font-medium transition-colors ${
                    formData.type === 'official'
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:border-gray-400'
                  }`}>
                  Official Photos
                </button>
              </div>
            </div>
            <div>
              <label htmlFor="theme-date" className="block text-sm font-medium text-gray-700 mb-1">
                Date {formData.type === 'pilot' ? '*' : '(optional)'}
              </label>
              <input id="theme-date" type="date" required={formData.type === 'pilot'} value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
            </div>
            <div>
              <label htmlFor="theme-title" className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
              <input id="theme-title" type="text" required value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="e.g., Golden Hour" />
            </div>
            <div>
              <label htmlFor="theme-desc" className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
              <textarea id="theme-desc" required value={formData.description} rows={3}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Describe the theme..." />
            </div>
            <div className="flex gap-3">
              <button type="button" onClick={() => setShowCreateForm(false)}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors">Cancel</button>
              <button type="submit"
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">Create Theme</button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-bold text-gray-900 mb-3">Themes</h3>

            <div className="space-y-2 mb-4">
              <select
                value={filterYear}
                onChange={e => { setFilterYear(e.target.value); setFilterDate('') }}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All years</option>
                {availableYears.map(y => <option key={y} value={y}>{y}</option>)}
              </select>
              <input
                type="date"
                value={filterDate}
                onChange={e => { setFilterDate(e.target.value); setFilterYear('') }}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              {(filterDate || filterYear) && (
                <button onClick={() => { setFilterDate(''); setFilterYear('') }}
                  className="text-xs text-blue-600 hover:text-blue-800">Clear filter</button>
              )}
            </div>

            <div className="space-y-2">
              {filteredThemes.length === 0 ? (
                <p className="text-gray-500 text-sm">No themes found</p>
              ) : filteredThemes.map((theme) => (
                <div key={theme.themeId}
                  className={`p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                    selectedTheme?.themeId === theme.themeId ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => setSelectedTheme(theme)}>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{theme.title}</span>
                        {theme.type === 'official' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">Official</span>
                        )}
                        {submissionCounts[theme.themeId] !== undefined && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                            📷 {submissionCounts[theme.themeId]}
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-500">{theme.date}</div>
                    </div>
                    <button onClick={(e) => { e.stopPropagation(); handleDeleteTheme(theme.themeId) }}
                      className="text-red-600 hover:text-red-900 text-sm">Delete</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div ref={detailRef} className="lg:col-span-2 lg:sticky lg:top-4 lg:self-start">
          {selectedTheme ? (
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">{selectedTheme.title}</h3>
                  <p className="text-sm text-gray-600">{selectedTheme.description}</p>
                  <p className="text-sm text-gray-500 mt-1">{selectedTheme.date}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleDownloadZip(selectedTheme.date)}
                    className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm">
                    Download ZIP
                  </button>
                  {submissions.length > 0 && (() => {
                    const unpublished = submissions.filter(s => !s.isPublished)
                    const allSelected = unpublished.length > 0 && unpublished.every(s => selectedSubmissions.has(s.submissionId))
                    return (
                      <button
                        onClick={() => {
                          if (allSelected) {
                            setSelectedSubmissions(new Set())
                          } else {
                            setSelectedSubmissions(new Set(unpublished.map(s => s.submissionId)))
                          }
                        }}
                        className="px-3 py-1 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                      >
                        {allSelected ? 'Deselect All' : 'Select All'}
                      </button>
                    )
                  })()}
                  {selectedSubmissions.size > 0 && (
                    <button onClick={handleBatchPublish}
                      className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm">
                      Publish Selected ({selectedSubmissions.size})
                    </button>
                  )}
                </div>
              </div>

              {selectedTheme.type === 'official' && (
                <div className="mb-6">
                  <h4 className="font-medium text-gray-900 mb-3">Upload Official Photos</h4>
                  <div
                    onDrop={handleDrop}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onClick={() => fileInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                      dragOver ? 'border-purple-500 bg-purple-50' : 'border-gray-300 hover:border-gray-400'
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      multiple
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => { if (e.target.files) { addBatchFiles(e.target.files); e.target.value = '' } }}
                    />
                    <p className="text-gray-600 text-sm">Drag & drop images here or click to select files</p>
                  </div>

                  {batchFiles.length > 0 && (
                    <div className="mt-4 space-y-3">
                      {batchFiles.map((bf) => (
                        <div key={bf.id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                          <span className="text-sm text-gray-700 truncate flex-shrink-0 w-40">{bf.file.name}</span>
                          <input
                            type="text"
                            placeholder="Caption (optional)"
                            value={bf.caption}
                            onChange={(e) => updateBatchCaption(bf.id, e.target.value)}
                            disabled={batchUploading}
                            className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-purple-500 focus:border-transparent"
                          />
                          {bf.status === 'uploading' && <span className="text-xs text-blue-600">Uploading…</span>}
                          {bf.status === 'done' && <span className="text-xs text-green-600">✓</span>}
                          {bf.status === 'error' && <span className="text-xs text-red-600">Failed</span>}
                          {!batchUploading && (
                            <button onClick={() => removeBatchFile(bf.id)} className="text-red-500 hover:text-red-700 text-sm">&times;</button>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={handleBatchUpload}
                        disabled={batchUploading}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 transition-colors text-sm"
                      >
                        {batchUploading ? 'Uploading…' : `Upload All (${batchFiles.length})`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <h4 className="font-medium text-gray-900 mb-3">Submissions ({submissions.length})</h4>
              {submissions.length === 0 ? (
                <p className="text-gray-500 text-sm">No submissions yet</p>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {submissions.map((sub) => (
                    <div key={sub.submissionId} className="relative border-2 border-gray-200 rounded-lg overflow-hidden">
                      <div className="aspect-square bg-gray-100 flex items-center justify-center">
                        {sub.photoUrl && !imgErrors.has(sub.submissionId) ? (
                          <img
                            src={sub.photoUrl}
                            alt={sub.fileName}
                            className="w-full h-full object-cover"
                            onError={() => setImgErrors(prev => new Set(prev).add(sub.submissionId))}
                          />
                        ) : (
                          <span className="text-gray-400 text-sm truncate px-2">{sub.fileName}</span>
                        )}
                      </div>
                      <div className="absolute top-2 left-2">
                        <input type="checkbox" checked={selectedSubmissions.has(sub.submissionId)}
                          onChange={() => toggleSelection(sub.submissionId)} className="w-5 h-5" />
                      </div>
                      <div className="p-2 bg-white">
                        <p className="text-xs text-gray-600 truncate">{sub.fileName}</p>
                        {sub.uploadedBy && (
                          <p className="text-xs text-purple-600 truncate" title={`Uploaded by ${sub.uploadedBy}`}>by {sub.uploadedBy}</p>
                        )}
                        {sub.caption && (
                          <p className="text-xs text-gray-400 truncate" title={sub.caption}>{sub.caption}</p>
                        )}
                        <div className="flex justify-between items-center mt-1">
                          <span className={`text-xs px-2 py-1 rounded ${sub.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {sub.isPublished ? 'Published' : 'Unpublished'}
                          </span>
                          <button onClick={() => sub.isPublished ? handleUnpublish(sub.submissionId) : handlePublish(sub.submissionId)}
                            className="text-xs text-blue-600 hover:text-blue-900">
                            {sub.isPublished ? 'Unpublish' : 'Publish'}
                          </button>
                        </div>
                        {sub.isPublished && (
                          <div className="mt-1">
                            {selectedTheme.winnerSubmissionId === sub.submissionId ? (
                              <div className="flex items-center justify-between">
                                <span className="text-xs px-2 py-1 rounded bg-yellow-100 text-yellow-800 font-medium">🏆 Winner</span>
                                <button onClick={() => handleRemoveWinner()}
                                  className="text-xs text-red-600 hover:text-red-800">Remove</button>
                              </div>
                            ) : (
                              <button onClick={() => handleSetWinner(sub.submissionId)}
                                className="text-xs text-yellow-700 hover:text-yellow-900">🏆 Set as Winner</button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-gray-500 text-center">Select a theme to view submissions</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
